import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import { keys } from "@/api/queryKeys";
import type {
  AgentModelCatalog,
  AgentProviderSummary,
  CodexModelCatalog,
  IntentSpec,
  ModeSpec,
} from "@/api/types";

/** Every agent provider registered on this backend. Drives the
 *  provider picker (hidden when ``length === 1``) and the
 *  "<provider> is thinking…" status indicators. Session-lifetime
 *  cache — a backend restart rolls the process and re-registers. */
export function useAgentProviders() {
  return useQuery({
    queryKey: keys.agentProviders,
    queryFn: async () =>
      (await api.get<AgentProviderSummary[]>("/agent/providers")).data,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/** Provider-scoped model catalog with feature flags the UI uses to
 *  decide which dropdowns to render. Complements the legacy codex-
 *  specific ``useCodexModels`` during the migration. */
export function useAgentProviderModels(slug: string | null | undefined) {
  return useQuery({
    queryKey: keys.agentProviderModels(slug ?? ""),
    queryFn: async () =>
      (
        await api.get<AgentModelCatalog>(
          `/agent/providers/${encodeURIComponent(slug ?? "")}/models`,
        )
      ).data,
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: Boolean(slug),
  });
}

export function useModes() {
  return useQuery({
    queryKey: ["sessions", "modes"],
    queryFn: async () => (await api.get<ModeSpec[]>("/sessions/modes")).data,
    staleTime: 60_000,
  });
}

export function useIntents() {
  return useQuery({
    queryKey: ["sessions", "intents"],
    queryFn: async () =>
      (await api.get<IntentSpec[]>("/sessions/intents")).data,
    staleTime: 60_000,
  });
}

export function useCodexModels() {
  return useQuery({
    queryKey: ["sessions", "codex-models"],
    queryFn: async () =>
      (await api.get<CodexModelCatalog>("/sessions/codex-models")).data,
    staleTime: 60_000,
  });
}
